import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

// Trade-specific subscription pricing (yearly with 25% discount already applied)
const TRADE_SUBSCRIPTION_PRICES: Record<string, number> = {
  'handyman': 495,        // £55/month × 12 = £660, 25% OFF = £495
  'plumber': 675,         // £75/month × 12 = £900, 25% OFF = £675
  'electrician': 630,     // £70/month × 12 = £840, 25% OFF = £630
  'waste-removal': 405,   // £45/month × 12 = £540, 25% OFF = £405
  'transportation': 720,  // £80/month × 12 = £960, 25% OFF = £720
  'cleaning': 360         // £40/month × 12 = £480, 25% OFF = £360
};

export default function ProvidersManagement({ admin }: { admin: any }) {
  const [providers, setProviders] = useState<any[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [activeView, setActiveView] = useState<'applications' | 'approved'>('applications');
  const [activeTrade, setActiveTrade] = useState<'all' | 'handyman' | 'plumber' | 'electrician' | 'waste-removal'>('all');
  const [showDocumentRequest, setShowDocumentRequest] = useState(false);
  const [documentRequestMessage, setDocumentRequestMessage] = useState('');
  const [requestedDocuments, setRequestedDocuments] = useState<string[]>([]);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [addProviderForm, setAddProviderForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    trade: 'handyman',
    address: '',
    city: '',
    postcode: '',
    hourly_rate: '25',
    mark_as_paid: true,
    auto_approve: true
  });
  const [addingProvider, setAddingProvider] = useState(false);

  useEffect(() => {
    loadProviders();
    
    // Set up real-time subscription for new applications
    const subscription = supabase
      .channel('provider_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'provider_profiles' },
        (payload) => {
          console.log('Provider change detected:', payload);
          loadProviders();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterProviders();
  }, [searchTerm, statusFilter, activeTrade, providers, activeView]);

  const loadProviders = async () => {
    const { data, error } = await supabase
      .from('provider_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setProviders(data);
    }
    setLoading(false);
  };

  const filterProviders = () => {
    let filtered = [...providers];

    // Filter by view (applications vs approved)
    if (activeView === 'applications') {
      filtered = filtered.filter(p => p.status === 'pending');
    } else {
      filtered = filtered.filter(p => p.status === 'approved' || p.status === 'active');
    }

    // Filter by trade category
    if (activeTrade !== 'all') {
      filtered = filtered.filter(p => p.trade === activeTrade);
    }

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    setFilteredProviders(filtered);
  };

  const updateProviderStatus = async (providerId: string, newStatus: string) => {
    const provider = providers.find(p => p.id === providerId);
    
    // Calculate payment deadline (48 hours from approval)
    const paymentDeadline = new Date();
    paymentDeadline.setHours(paymentDeadline.getHours() + 48);
    
    // Get correct subscription price for this trade
    const subscriptionPrice = TRADE_SUBSCRIPTION_PRICES[provider.trade] || 495;
    
    // Prepare update data based on the new status
    let updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (newStatus === 'approved') {
      // When approving, set both application_status and status to approved
      updateData.application_status = 'approved';
      updateData.status = 'approved';
      updateData.approved_at = new Date().toISOString();
      updateData.payment_deadline = paymentDeadline.toISOString();
    } else if (newStatus === 'active') {
      // When setting to active, ONLY mark subscription as paid (not workwear)
      const hasSubscription = provider.subscription_paid === true;
      
      console.log('Activating subscription only - Current provider status:', {
        subscription_paid: provider.subscription_paid,
        workwear_purchased: provider.workwear_purchased,
        hasSubscription,
        trade: provider.trade,
        subscriptionPrice
      });
      
      // Always set these core fields
      updateData.status = 'active';
      updateData.is_active = true;
      updateData.application_status = 'approved';
      
      // Mark subscription as paid if not already (ONLY subscription, NOT workwear)
      if (!hasSubscription) {
        updateData.subscription_paid = true;
        updateData.subscription_payment_date = new Date().toISOString();
        updateData.subscription_amount = subscriptionPrice; // Use correct trade-specific price
        console.log(`Marking subscription as paid (£${subscriptionPrice}) for ${provider.trade} - Workwear NOT included`);
      }
      
      console.log('Final update data (subscription only):', updateData);
    } else if (newStatus === 'suspended') {
      // When suspending, only change status
      updateData.status = 'suspended';
      updateData.is_active = false;
    } else if (newStatus === 'rejected') {
      // When rejecting, set both
      updateData.application_status = 'rejected';
      updateData.status = 'rejected';
    } else if (newStatus === 'pending') {
      // When moving back to pending
      updateData.application_status = 'pending';
      updateData.status = 'pending';
    }
    
    console.log('Updating provider with data:', updateData);
    
    const { data: updatedData, error } = await supabase
      .from('provider_profiles')
      .update(updateData)
      .eq('id', providerId)
      .select()
      .single();

    console.log('Update result:', { updatedData, error });

    if (!error) {
      // Send notification and email if approved
      if (newStatus === 'approved') {
        // Create notification
        await supabase.from('notifications').insert({
          user_id: provider.user_id,
          title: 'Application Approved! 🎉',
          message: `Congratulations! Your application has been approved. Please complete your subscription payment within 48 hours to activate your account. Deadline: ${paymentDeadline.toLocaleString()}`,
          type: 'application_approved',
          read: false,
          created_at: new Date().toISOString()
        });

        // Send approval email via edge function
        try {
          await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-approval-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              providerId: provider.id,
              providerEmail: provider.email,
              providerName: provider.full_name || `${provider.first_name} ${provider.last_name}`,
              trade: provider.trade
            })
          });
        } catch (emailError) {
          console.error('Email sending failed:', emailError);
        }

        alert(`✅ Provider approved!\n\n📧 Email notification sent to ${provider.email}\n⏰ Payment deadline: ${paymentDeadline.toLocaleString()}\n\nThey have 48 hours to complete their subscription payment.`);
      } else if (newStatus === 'rejected') {
        // Create rejection notification
        await supabase.from('notifications').insert({
          user_id: provider.user_id,
          title: 'Application Update',
          message: 'Thank you for your interest in becoming a Taskbudy provider. Unfortunately, your application was not approved at this time. Please contact support for more information.',
          type: 'application_rejected',
          read: false,
          created_at: new Date().toISOString()
        });
      } else if (newStatus === 'active') {
        // Create activation notification
        await supabase.from('notifications').insert({
          user_id: provider.user_id,
          title: 'Subscription Activated! 🎉',
          message: 'Your subscription has been activated! You can now start accepting jobs.',
          type: 'subscription_activated',
          read: false,
          created_at: new Date().toISOString()
        });
        
        // Build detailed confirmation message
        let confirmMessage = '✅ Subscription activated!\n\n';
        confirmMessage += '✓ Status: Active\n';
        confirmMessage += '✓ Application: Approved\n';
        
        if (!provider.subscription_paid) {
          confirmMessage += `✓ Subscription: Marked as Paid (£${subscriptionPrice}/year)\n`;
          confirmMessage += `✓ Trade: ${provider.trade.replace('-', ' ').toUpperCase()}\n`;
        } else {
          confirmMessage += `✓ Subscription: Already Paid (£${provider.subscription_amount || subscriptionPrice}/year)\n`;
        }
        
        confirmMessage += '✓ Provider can now accept jobs\n';
        
        if (!provider.workwear_purchased) {
          confirmMessage += '\n⚠️ Note: Workwear is NOT included (£99.99)\n';
          confirmMessage += '• Provider needs to purchase workwear separately\n';
          confirmMessage += '• OR use "Mark Workwear as Paid" button to give it for free';
        } else {
          confirmMessage += '\n✓ Workwear: Already Purchased (£99.99)';
        }
        
        alert(confirmMessage);
      }
      
      loadProviders();
      if (selectedProvider?.id === providerId) {
        const updatedProvider = { ...selectedProvider, ...updateData };
        setSelectedProvider(updatedProvider);
      }
    } else {
      console.error('Update error:', error);
      alert(`❌ Failed to update provider status: ${error.message}`);
    }
  };

  const activateSubscription = async (providerId: string, tradeType: string) => {
    try {
      const subscriptionPrice = TRADE_SUBSCRIPTION_PRICES[tradeType as keyof typeof TRADE_SUBSCRIPTION_PRICES] || 495;
      const vatAmount = subscriptionPrice * 0.20;
      const totalAmount = subscriptionPrice + vatAmount;
      const subscriptionPeriod = `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;

      const { error } = await supabase
        .from('provider_profiles')
        .update({
          subscription_status: 'active',
          application_status: 'approved',
          subscription_paid_at: new Date().toISOString(),
          subscription_amount: subscriptionPrice,
        })
        .eq('id', providerId);

      if (error) throw error;

      // Generate invoice for manual activation
      const { error: invoiceError } = await supabase.functions.invoke('generate-subscription-invoice', {
        body: {
          providerId,
          invoiceType: 'subscription',
          tradeType,
          amount: subscriptionPrice,
          vatAmount,
          discountAmount: 0,
          discountCode: null,
          totalAmount,
          paymentMethod: 'admin_manual',
          stripePaymentId: null,
          subscriptionPeriod
        }
      });

      if (invoiceError) {
        console.error('Error generating invoice:', invoiceError);
      }

      alert(`✅ Subscription activated!\n\n✓ Status: Active\n✓ Application: Approved\n✓ Subscription: Marked as Paid (£${subscriptionPrice}/year)\n✓ Trade: ${tradeType}\n✓ Invoice: Generated\n✓ Provider can now accept jobs\n\n⚠️ Note: Workwear is NOT included (£99.99)`);
      loadProviders();
    } catch (error: any) {
      alert('Error activating subscription: ' + error.message);
    }
  };

  const markWorkwearAsPaid = async (providerId: string) => {
    try {
      const provider = providers.find(p => p.id === providerId);
      if (!provider) return;

      const tradeType = provider.trade;
      const workwearAmount = 99.99;
      const vatAmount = workwearAmount * 0.20;
      const totalAmount = workwearAmount + vatAmount;

      const { error } = await supabase
        .from('provider_profiles')
        .update({
          workwear_purchased: true,
          workwear_paid_at: new Date().toISOString(),
        })
        .eq('id', providerId);

      if (error) throw error;

      // Generate invoice for workwear
      const { error: invoiceError } = await supabase.functions.invoke('generate-subscription-invoice', {
        body: {
          providerId,
          invoiceType: 'workwear',
          tradeType,
          amount: workwearAmount,
          vatAmount,
          discountAmount: 0,
          discountCode: null,
          totalAmount,
          paymentMethod: 'admin_manual',
          stripePaymentId: null,
          subscriptionPeriod: null
        }
      });

      if (invoiceError) {
        console.error('Error generating invoice:', invoiceError);
      }

      alert(`✅ Workwear marked as paid!\n\n✓ Workwear Kit: Granted (£99.99)\n✓ Invoice: Generated\n✓ Provider can now receive workwear`);
      loadProviders();
    } catch (error: any) {
      alert('Error marking workwear as paid: ' + error.message);
    }
  };

  const handleAddProvider = async () => {
    // Validate required fields
    if (!addProviderForm.email || !addProviderForm.password || !addProviderForm.first_name || !addProviderForm.last_name) {
      alert('❌ Please fill in all required fields (Email, Password, First Name, Last Name)');
      return;
    }

    if (addProviderForm.password.length < 6) {
      alert('❌ Password must be at least 6 characters long');
      return;
    }

    setAddingProvider(true);

    try {
      // Call the create-provider-account edge function
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-provider-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          email: addProviderForm.email,
          password: addProviderForm.password,
          firstName: addProviderForm.first_name,
          lastName: addProviderForm.last_name,
          phone: addProviderForm.phone,
          trade: addProviderForm.trade,
          address: addProviderForm.address,
          city: addProviderForm.city,
          postcode: addProviderForm.postcode,
          hourlyRate: parseFloat(addProviderForm.hourly_rate) || 25,
          autoApprove: addProviderForm.auto_approve,
          markAsPaid: addProviderForm.mark_as_paid
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create provider account');
      }

      alert(`✅ Provider account created successfully!\n\n📧 Email: ${addProviderForm.email}\n🔑 Password: ${addProviderForm.password}\n\n${addProviderForm.auto_approve ? '✅ Account approved' : '⏳ Pending approval'}\n${addProviderForm.mark_as_paid ? '✅ Subscription marked as paid' : '⏳ Payment required'}`);
      
      setShowAddProvider(false);
      setAddProviderForm({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        trade: 'handyman',
        address: '',
        city: '',
        postcode: '',
        hourly_rate: '25',
        mark_as_paid: true,
        auto_approve: true
      });
      
      loadProviders();
    } catch (error: any) {
      console.error('Error creating provider:', error);
      alert(`❌ Failed to create provider: ${error.message}`);
    } finally {
      setAddingProvider(false);
    }
  };

  const sendDocumentRequest = async () => {
    if (!selectedProvider || requestedDocuments.length === 0) {
      alert('Please select at least one document type to request');
      return;
    }

    const { error } = await supabase
      .from('provider_profiles')
      .update({
        documents_requested: true,
        documents_request_date: new Date().toISOString(),
        application_notes: documentRequestMessage
      })
      .eq('id', selectedProvider.id);

    if (!error) {
      // Create notification
      await supabase.from('notifications').insert({
        user_id: selectedProvider.user_id,
        title: 'Documents Required',
        message: `Please upload the following documents: ${requestedDocuments.join(', ')}. ${documentRequestMessage}`,
        type: 'document_request',
        read: false
      });

      alert('✅ Document request sent successfully!');
      setShowDocumentRequest(false);
      setDocumentRequestMessage('');
      setRequestedDocuments([]);
      loadProviders();
    }
  };

  const viewProviderDetails = (provider: any) => {
    setSelectedProvider(provider);
    setShowDetails(true);
  };

  const pendingCount = providers.filter(p => p.status === 'pending').length;
  const approvedCount = providers.filter(p => p.status === 'approved' || p.status === 'active').length;
  const awaitingPaymentCount = providers.filter(p => 
    (p.status === 'approved' || p.status === 'active') && !p.subscription_paid
  ).length;
  const awaitingWorkwearCount = providers.filter(p => 
    (p.status === 'approved' || p.status === 'active') && p.subscription_paid && !p.workwear_purchased
  ).length;
  const awaitingDocumentsCount = providers.filter(p => 
    p.documents_requested && (
      p.dbs_certificate_status === 'pending' || 
      p.insurance_certificate_status === 'pending'
    )
  ).length;

  // Count providers by trade (only approved/active)
  const approvedProviders = providers.filter(p => p.status === 'approved' || p.status === 'active');
  const handymanCount = approvedProviders.filter(p => p.trade === 'handyman').length;
  const plumberCount = approvedProviders.filter(p => p.trade === 'plumber').length;
  const electricianCount = approvedProviders.filter(p => p.trade === 'electrician').length;
  const wasteRemovalCount = approvedProviders.filter(p => p.trade === 'waste-removal').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Service Providers Management</h2>
            <p className="text-gray-600 mt-1">Review applications and manage service providers</p>
          </div>
          <button
            onClick={() => setShowAddProvider(true)}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer font-medium flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-user-add-line text-xl w-5 h-5 flex items-center justify-center"></i>
            Add Provider
          </button>
        </div>
      </div>

      {/* Alert Notifications */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90">New Applications</p>
              <p className="text-2xl font-bold text-white">{pendingCount}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-file-list-3-line text-white text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90">Awaiting Payment</p>
              <p className="text-2xl font-bold text-white">{awaitingPaymentCount}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-bank-card-line text-white text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90">Awaiting Workwear</p>
              <p className="text-2xl font-bold text-white">{awaitingWorkwearCount}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-shirt-line text-white text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90">Awaiting Documents</p>
              <p className="text-2xl font-bold text-white">{awaitingDocumentsCount}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-file-shield-line text-white text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex gap-3">
          <button
            onClick={() => setActiveView('applications')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
              activeView === 'applications'
                ? 'bg-orange-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <i className="ri-file-list-3-line text-xl w-5 h-5 flex items-center justify-center"></i>
              <span>New Applications</span>
              {pendingCount > 0 && (
                <span className="px-2 py-1 bg-red-500 text-white rounded-full text-xs font-bold animate-pulse">
                  {pendingCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveView('approved')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
              activeView === 'approved'
                ? 'bg-teal-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <i className="ri-team-line text-xl w-5 h-5 flex items-center justify-center"></i>
              <span>Approved Providers</span>
              <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-bold">
                {approvedCount}
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Trade Category Tabs - Only show for Approved Providers */}
      {activeView === 'approved' && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setActiveTrade('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTrade === 'all'
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <i className="ri-team-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>All Providers</span>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  activeTrade === 'all' ? 'bg-white/20' : 'bg-teal-100 text-teal-700'
                }`}>
                  {approvedCount}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTrade('handyman')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTrade === 'handyman'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <i className="ri-tools-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>Handyman</span>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  activeTrade === 'handyman' ? 'bg-white/20' : 'bg-blue-100 text-blue-700'
                }`}>
                  {handymanCount}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTrade('plumber')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTrade === 'plumber'
                  ? 'bg-cyan-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <i className="ri-drop-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>Plumber</span>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  activeTrade === 'plumber' ? 'bg-white/20' : 'bg-cyan-100 text-cyan-700'
                }`}>
                  {plumberCount}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTrade('electrician')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTrade === 'electrician'
                  ? 'bg-yellow-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <i className="ri-flashlight-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>Electrician</span>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  activeTrade === 'electrician' ? 'bg-white/20' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {electricianCount}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTrade('waste-removal')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTrade === 'waste-removal'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <i className="ri-delete-bin-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>Waste Removal</span>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  activeTrade === 'waste-removal' ? 'bg-white/20' : 'bg-green-100 text-green-700'
                }`}>
                  {wasteRemovalCount}
                </span>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Providers</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg w-5 h-5 flex items-center justify-center"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
          {activeView === 'approved' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="all">All Statuses</option>
                <option value="approved">Approved</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Providers List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {filteredProviders.length === 0 ? (
          <div className="text-center py-12">
            <i className={`${activeView === 'applications' ? 'ri-file-list-3-line' : 'ri-team-line'} text-6xl text-gray-300 mb-4`}></i>
            <p className="text-gray-500 text-lg">
              {activeView === 'applications' 
                ? 'No pending applications at the moment' 
                : activeTrade === 'all'
                ? 'No approved providers yet'
                : `No ${activeTrade.replace('-', ' ')} providers yet`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProviders.map((provider) => (
                  <tr key={provider.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                          provider.trade === 'handyman' ? 'bg-blue-500' :
                          provider.trade === 'plumber' ? 'bg-cyan-500' :
                          provider.trade === 'electrician' ? 'bg-yellow-500' :
                          provider.trade === 'waste-removal' ? 'bg-green-500' :
                          'bg-teal-500'
                        }`}>
                          {(provider.full_name || provider.first_name || 'P')[0].toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {provider.full_name || `${provider.first_name} ${provider.last_name}`}
                          </div>
                          <div className="text-sm text-gray-500">ID: {provider.id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{provider.email}</div>
                      <div className="text-sm text-gray-500">{provider.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        provider.trade === 'handyman' ? 'bg-blue-100 text-blue-800' :
                        provider.trade === 'plumber' ? 'bg-cyan-100 text-cyan-800' :
                        provider.trade === 'electrician' ? 'bg-yellow-100 text-yellow-800' :
                        provider.trade === 'waste-removal' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {provider.trade?.replace('-', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          {provider.subscription_paid ? (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <i className="ri-check-line"></i> Subscription Paid
                            </span>
                          ) : (
                            <span className="text-xs text-red-600 flex items-center gap-1">
                              <i className="ri-close-line"></i> Pending Payment
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {provider.workwear_purchased ? (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <i className="ri-check-line"></i> Workwear Purchased
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <i className="ri-close-line"></i> No Workwear
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          {provider.dbs_certificate_status === 'approved' ? (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <i className="ri-check-line"></i> DBS
                            </span>
                          ) : provider.dbs_certificate_status === 'pending' ? (
                            <span className="text-xs text-orange-600 flex items-center gap-1">
                              <i className="ri-time-line"></i> DBS
                            </span>
                          ) : (
                            <span className="text-xs text-red-600 flex items-center gap-1">
                              <i className="ri-close-line"></i> DBS
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {provider.insurance_certificate_status === 'approved' ? (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <i className="ri-check-line"></i> Insurance
                            </span>
                          ) : provider.insurance_certificate_status === 'pending' ? (
                            <span className="text-xs text-orange-600 flex items-center gap-1">
                              <i className="ri-time-line"></i> Insurance
                            </span>
                          ) : (
                            <span className="text-xs text-red-600 flex items-center gap-1">
                              <i className="ri-close-line"></i> Insurance
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        provider.status === 'approved' || provider.status === 'active' ? 'bg-green-100 text-green-800' :
                        provider.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        provider.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {provider.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => viewProviderDetails(provider)}
                        className="text-teal-600 hover:text-teal-900 font-medium cursor-pointer whitespace-nowrap"
                      >
                        {activeView === 'applications' ? 'Review Application' : 'View Details'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Provider Details Modal */}
      {showDetails && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] overflow-y-auto my-8">
            <div className="sticky top-0 bg-gradient-to-r from-teal-50 to-cyan-50 p-6 border-b border-gray-200 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {activeView === 'applications' ? 'Application Review' : 'Provider Details'}
                  </h3>
                  <p className="text-gray-600 mt-1">
                    {selectedProvider.trade?.replace('-', ' ').toUpperCase()} - {selectedProvider.status}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Application Status Banner */}
              {activeView === 'applications' && (
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                  <div className="flex items-center">
                    <i className="ri-alert-line text-orange-500 text-2xl mr-3 w-6 h-6 flex items-center justify-center"></i>
                    <div>
                      <h4 className="font-semibold text-orange-900">New Application Pending Review</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        Please review all details carefully before approving or rejecting this application.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment & Workwear Status */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg border-2 ${
                  selectedProvider.subscription_paid 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-bank-card-line text-xl"></i>
                        Subscription Payment
                      </h4>
                      <p className={`text-sm mt-1 ${
                        selectedProvider.subscription_paid ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {selectedProvider.subscription_paid 
                          ? `✅ Paid on ${new Date(selectedProvider.subscription_payment_date).toLocaleDateString()}`
                          : '❌ Payment Pending'
                        }
                      </p>
                      {selectedProvider.subscription_amount && (
                        <p className="text-sm font-bold text-gray-900 mt-1">
                          Amount: £{selectedProvider.subscription_amount}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-lg border-2 ${
                  selectedProvider.workwear_purchased 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-shirt-line text-xl"></i>
                        Workwear Kit
                      </h4>
                      <p className={`text-sm mt-1 ${
                        selectedProvider.workwear_purchased ? 'text-green-700' : 'text-gray-700'
                      }`}>
                        {selectedProvider.workwear_purchased 
                          ? `✅ Purchased on ${new Date(selectedProvider.workwear_purchase_date).toLocaleDateString()}`
                          : '⏳ Not Purchased Yet'
                        }
                      </p>
                      {selectedProvider.workwear_amount && (
                        <p className="text-sm font-bold text-gray-900 mt-1">
                          Amount: £{selectedProvider.workwear_amount}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Status */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-file-shield-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Required Documents
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-lg border-2 ${
                    selectedProvider.dbs_certificate_status === 'approved' 
                      ? 'bg-green-50 border-green-200' 
                      : selectedProvider.dbs_certificate_status === 'pending'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <h5 className="font-medium text-gray-900 mb-2">DBS Certificate</h5>
                    <p className="text-sm mb-2">
                      Status: <span className="font-semibold">{selectedProvider.dbs_certificate_status || 'Not Submitted'}</span>
                    </p>
                    {selectedProvider.dbs_certificate_url && (
                      <a 
                        href={selectedProvider.dbs_certificate_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-teal-600 hover:text-teal-800 underline"
                      >
                        View Document
                      </a>
                    )}
                  </div>

                  <div className={`p-4 rounded-lg border-2 ${
                    selectedProvider.insurance_certificate_status === 'approved' 
                      ? 'bg-green-50 border-green-200' 
                      : selectedProvider.insurance_certificate_status === 'pending'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <h5 className="font-medium text-gray-900 mb-2">Insurance Certificate</h5>
                    <p className="text-sm mb-2">
                      Status: <span className="font-semibold">{selectedProvider.insurance_certificate_status || 'Not Submitted'}</span>
                    </p>
                    {selectedProvider.insurance_certificate_url && (
                      <a 
                        href={selectedProvider.insurance_certificate_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-teal-600 hover:text-teal-800 underline"
                      >
                        View Document
                      </a>
                    )}
                  </div>

                  <div className="p-4 rounded-lg border-2 bg-blue-50 border-blue-200">
                    <h5 className="font-medium text-gray-900 mb-2">Qualifications</h5>
                    <p className="text-sm mb-2">
                      Uploaded: <span className="font-semibold">
                        {selectedProvider.qualification_certificates?.length || 0} files
                      </span>
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={() => setShowDocumentRequest(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer font-medium flex items-center gap-2"
                  >
                    <i className="ri-mail-send-line"></i>
                    Request Documents
                  </button>
                </div>
              </div>

              {/* Basic Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-user-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Basic Information
                </h4>
                
                {/* Selfie Photo Display */}
                {(selectedProvider.selfie_url || selectedProvider.profile_photo_url) && (
                  <div className="mb-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border-2 border-purple-200">
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg flex-shrink-0">
                        <img 
                          src={selectedProvider.selfie_url || selectedProvider.profile_photo_url} 
                          alt="Provider Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('❌ Failed to load profile photo for provider:', selectedProvider.id);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                          <i className="ri-user-smile-line text-purple-600"></i>
                          Profile Photo
                        </h5>
                        <p className="text-sm text-gray-600 mb-2">Provider's profile photo</p>
                        <button
                          onClick={() => window.open(selectedProvider.selfie_url || selectedProvider.profile_photo_url, '_blank')}
                          className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer text-xs font-medium whitespace-nowrap"
                        >
                          <i className="ri-eye-line mr-1"></i>
                          View Full Size
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="text-sm text-gray-600">Full Name</label>
                    <p className="font-medium text-gray-900">
                      {selectedProvider.full_name || `${selectedProvider.first_name} ${selectedProvider.last_name}`}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Email</label>
                    <p className="font-medium text-gray-900">{selectedProvider.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Phone</label>
                    <p className="font-medium text-gray-900">{selectedProvider.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Trade Category</label>
                    <p className="font-medium text-gray-900">
                      {selectedProvider.trade?.replace('-', ' ').toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-map-pin-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Address Information
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900">{selectedProvider.address || 'N/A'}</p>
                  <p className="text-gray-600">{selectedProvider.city} {selectedProvider.postcode}</p>
                </div>
              </div>

              {/* Professional Details */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-briefcase-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Professional Details
                </h4>
                <div className="grid md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="text-sm text-gray-600">Experience</label>
                    <p className="font-medium text-gray-900">{selectedProvider.experience || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Certifications</label>
                    <p className="font-medium text-gray-900">{selectedProvider.certifications || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Insurance</label>
                    <p className="font-medium text-gray-900">
                      {selectedProvider.insurance ? '✅ Yes' : '❌ No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Hourly Rate</label>
                    <p className="font-medium text-gray-900">£{selectedProvider.hourly_rate || 0}/hour</p>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedProvider.application_notes && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-sticky-note-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Admin Notes
                  </h4>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-gray-900">{selectedProvider.application_notes}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-gray-900 mb-4">
                  {activeView === 'applications' ? 'Application Decision' : 'Update Status'}
                </h4>
                <div className="flex flex-wrap gap-3">
                  {selectedProvider.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          if (confirm(`Approve ${selectedProvider.full_name || selectedProvider.first_name} as a ${selectedProvider.trade?.replace('-', ' ')}?\n\nThey will receive an email to complete payment.`)) {
                            updateProviderStatus(selectedProvider.id, 'approved');
                            setShowDetails(false);
                          }
                        }}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                      >
                        <i className="ri-check-line text-xl w-5 h-5 flex items-center justify-center"></i>
                        Approve Application
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Reject ${selectedProvider.full_name || selectedProvider.first_name}'s application?`)) {
                            updateProviderStatus(selectedProvider.id, 'rejected');
                            setShowDetails(false);
                          }
                        }}
                        className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                      >
                        <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center"></i>
                        Reject Application
                      </button>
                    </>
                  )}
                  {(selectedProvider.status === 'approved' || selectedProvider.status === 'active') && (
                    <>
                      <button
                        onClick={() => updateProviderStatus(selectedProvider.id, 'suspended')}
                        className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                      >
                        <i className="ri-pause-circle-line text-xl w-5 h-5 flex items-center justify-center"></i>
                        Suspend Provider
                      </button>
                      <button
                        onClick={() => updateProviderStatus(selectedProvider.id, 'active')}
                        className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                      >
                        <i className="ri-check-double-line text-xl w-5 h-5 flex items-center justify-center"></i>
                        Activate Subscription
                      </button>
                      {!selectedProvider.workwear_purchased && (
                        <button
                          onClick={() => markWorkwearAsPaid(selectedProvider.id)}
                          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                        >
                          <i className="ri-shirt-line text-xl w-5 h-5 flex items-center justify-center"></i>
                          Mark Workwear as Paid
                        </button>
                      )}
                    </>
                  )}
                  {selectedProvider.status === 'suspended' && (
                    <button
                      onClick={() => updateProviderStatus(selectedProvider.id, 'approved')}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                    >
                      <i className="ri-restart-line text-xl w-5 h-5 flex items-center justify-center"></i>
                      Reactivate Provider
                    </button>
                  )}
                  {selectedProvider.status === 'rejected' && (
                    <button
                      onClick={() => updateProviderStatus(selectedProvider.id, 'pending')}
                      className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                    >
                      <i className="ri-arrow-go-back-line text-xl w-5 h-5 flex items-center justify-center"></i>
                      Move to Pending
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Request Modal */}
      {showDocumentRequest && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Request Documents</h3>
              <button
                onClick={() => {
                  setShowDocumentRequest(false);
                  setRequestedDocuments([]);
                  setDocumentRequestMessage('');
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Required Documents
                </label>
                <div className="space-y-2">
                  {['DBS Certificate', 'Insurance Certificate', 'Qualification Certificates', 'Proof of Address'].map((doc) => (
                    <label key={doc} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={requestedDocuments.includes(doc)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setRequestedDocuments([...requestedDocuments, doc]);
                          } else {
                            setRequestedDocuments(requestedDocuments.filter(d => d !== doc));
                          }
                        }}
                        className="w-4 h-4 text-teal-600 cursor-pointer"
                      />
                      <span className="font-medium text-gray-900">{doc}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Message (Optional)
                </label>
                <textarea
                  value={documentRequestMessage}
                  onChange={(e) => setDocumentRequestMessage(e.target.value)}
                  placeholder="Add any specific instructions or requirements..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {documentRequestMessage.length}/500 characters
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={sendDocumentRequest}
                  disabled={requestedDocuments.length === 0}
                  className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send Request
                </button>
                <button
                  onClick={() => {
                    setShowDocumentRequest(false);
                    setRequestedDocuments([]);
                    setDocumentRequestMessage('');
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Provider Modal */}
      {showAddProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[95vh] overflow-y-auto my-8">
            <div className="sticky top-0 bg-gradient-to-r from-teal-50 to-cyan-50 p-6 border-b border-gray-200 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Add New Provider</h3>
                  <p className="text-gray-600 mt-1">Manually create a provider account without payment</p>
                </div>
                <button
                  onClick={() => {
                    setShowAddProvider(false);
                    setAddProviderForm({
                      email: '',
                      password: '',
                      first_name: '',
                      last_name: '',
                      phone: '',
                      trade: 'handyman',
                      address: '',
                      city: '',
                      postcode: '',
                      hourly_rate: '25',
                      mark_as_paid: true,
                      auto_approve: true
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Account Information */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-lock-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Account Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={addProviderForm.email}
                      onChange={(e) => setAddProviderForm({...addProviderForm, email: e.target.value})}
                      placeholder="provider@example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={addProviderForm.password}
                      onChange={(e) => setAddProviderForm({...addProviderForm, password: e.target.value})}
                      placeholder="Minimum 6 characters"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-user-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Personal Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={addProviderForm.first_name}
                      onChange={(e) => setAddProviderForm({...addProviderForm, first_name: e.target.value})}
                      placeholder="John"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={addProviderForm.last_name}
                      onChange={(e) => setAddProviderForm({...addProviderForm, last_name: e.target.value})}
                      placeholder="Smith"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={addProviderForm.phone}
                      onChange={(e) => setAddProviderForm({...addProviderForm, phone: e.target.value})}
                      placeholder="+44 7700 900000"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trade Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={addProviderForm.trade}
                      onChange={(e) => setAddProviderForm({...addProviderForm, trade: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="handyman">Handyman</option>
                      <option value="plumber">Plumber</option>
                      <option value="electrician">Electrician</option>
                      <option value="waste-removal">Waste Removal</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-map-pin-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Address Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                    <input
                      type="text"
                      value={addProviderForm.address}
                      onChange={(e) => setAddProviderForm({...addProviderForm, address: e.target.value})}
                      placeholder="123 Main Street"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={addProviderForm.city}
                      onChange={(e) => setAddProviderForm({...addProviderForm, city: e.target.value})}
                      placeholder="London"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Postcode</label>
                    <input
                      type="text"
                      value={addProviderForm.postcode}
                      onChange={(e) => setAddProviderForm({...addProviderForm, postcode: e.target.value.toUpperCase()})}
                      placeholder="SW1A 1AA"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Professional Details */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-briefcase-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Professional Details
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hourly Rate (£)</label>
                  <input
                    type="number"
                    value={addProviderForm.hourly_rate}
                    onChange={(e) => setAddProviderForm({...addProviderForm, hourly_rate: e.target.value})}
                    placeholder="25"
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Account Settings */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-settings-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Account Settings
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addProviderForm.auto_approve}
                      onChange={(e) => setAddProviderForm({...addProviderForm, auto_approve: e.target.checked})}
                      className="w-5 h-5 text-teal-600 cursor-pointer"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Auto-approve account</span>
                      <p className="text-sm text-gray-600">Account will be approved immediately</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addProviderForm.mark_as_paid}
                      onChange={(e) => setAddProviderForm({...addProviderForm, mark_as_paid: e.target.checked})}
                      className="w-5 h-5 text-teal-600 cursor-pointer"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Mark subscription as paid</span>
                      <p className="text-sm text-gray-600">Skip payment requirement (£49.99 subscription)</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={handleAddProvider}
                  disabled={addingProvider}
                  className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {addingProvider ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line"></i>
                      Create Provider
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAddProvider(false);
                    setAddProviderForm({
                      email: '',
                      password: '',
                      first_name: '',
                      last_name: '',
                      phone: '',
                      trade: 'handyman',
                      address: '',
                      city: '',
                      postcode: '',
                      hourly_rate: '25',
                      mark_as_paid: true,
                      auto_approve: true
                    });
                  }}
                  disabled={addingProvider}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
